import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "common/components/CustomButtons/Button";
import ErrorBox from "common/components/ErrorBox";
import LoadingBox from "common/components/LoadingBox";
import * as api from "core-module/services/api";
import config from "config.js";
import OAuthLogin from "./OAuthLogin";
import { restoreApiAccessFromFirebase } from "../firebaseApiAuth";
import { enableFirebaseAuthMode } from "../authMode";

const SIGNUP_REFLINK_STORAGE_KEY = "newuserSignupReflink";
const PENDING_CONTACT_ID_STORAGE_KEY = "newuserPendingFinalizeContactId";
const LINKED_CONTACT_ID_STORAGE_KEY = "newuserLinkedContactId";
const INVALID_REFLINK_MESSAGE = "Invalid or expired signup reference link.";
const PREINSTALL_ENABLE_ATTEMPTS = 10;
const PREINSTALL_ENABLE_RETRY_DELAY_MS = 1000;

const NewUserLinkAccount = () => {
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("");
  const didAutoStart = useRef(false);
  const isLinking = useRef(false);

  const queryReflink = new URLSearchParams(window.location.search).get(
    "signupreflink"
  );
  const storedReflink = window.localStorage.getItem(SIGNUP_REFLINK_STORAGE_KEY);
  const reflink = queryReflink || storedReflink;
  const legacyReturnPath =
    window.location.pathname.replace(config.MYACCOUNT.APP_PATH, "") +
    window.location.search;

  const isPreinstallEnabled = useCallback(async (linkedContactId) => {
    for (let attempt = 0; attempt < PREINSTALL_ENABLE_ATTEMPTS; attempt++) {
      const serviceIdentification = await api.get(
        "user." + linkedContactId + "/serviceidentification"
      );
      const hasPreinstall = serviceIdentification?.list?.[0]?.hasPreinstall;
      const hasPreinstallText = String(hasPreinstall).toLowerCase();
      if (
        hasPreinstall === true ||
        hasPreinstall === 1 ||
        hasPreinstallText === "1" ||
        hasPreinstallText === "true" ||
        hasPreinstallText === "yes" ||
        hasPreinstallText === "y"
      ) {
        return true;
      }

      if (attempt < PREINSTALL_ENABLE_ATTEMPTS - 1) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, PREINSTALL_ENABLE_RETRY_DELAY_MS)
        );
      }
    }
    return false;
  }, []);

  const enablePreinstallChecklist = useCallback(
    async (linkedContactId) => {
      try {
        const numericLinkedContactId = Number(linkedContactId);
        if (Number.isNaN(numericLinkedContactId)) {
          return false;
        }
        const response = await api.post("me.1/preinstallchecklist", {
          contactid: numericLinkedContactId,
        });
        if (!response || response?.success === false || response?.error > 0) {
          return false;
        }

        return isPreinstallEnabled(numericLinkedContactId);
      } catch (e) {
        console.warn(e.message || "Unable to enable preinstall checklist.");
        return false;
      }
    },
    [isPreinstallEnabled]
  );

  const completeLinkedAccount = useCallback((linkedContactId) => {
    window.localStorage.setItem(LINKED_CONTACT_ID_STORAGE_KEY, linkedContactId);
    window.localStorage.removeItem(PENDING_CONTACT_ID_STORAGE_KEY);
    window.localStorage.removeItem(SIGNUP_REFLINK_STORAGE_KEY);
    window.location.replace(config.MYACCOUNT.APP_PATH + "/");
  }, []);

  const finishLinkedAccount = useCallback(
    async (linkedContactId, preinstallChecklistEnabled = false) => {
      if (!linkedContactId) {
        setMessage("Could not identify the linked account.");
        setStatus("error");
        return false;
      }

      window.localStorage.setItem(
        PENDING_CONTACT_ID_STORAGE_KEY,
        linkedContactId
      );
      await api.post("me.1/revoke");
      const hasFirebaseAccess = await restoreApiAccessFromFirebase();
      if (!hasFirebaseAccess) {
        setStatus("needsAuth");
        return false;
      }

      enableFirebaseAuthMode();
      if (!preinstallChecklistEnabled) {
        const preinstallEnabled = await enablePreinstallChecklist(
          linkedContactId
        );
        if (!preinstallEnabled) {
          setMessage("Unable to enable the pre-install checklist.");
          setStatus("error");
          return false;
        }
      }
      completeLinkedAccount(linkedContactId);
      return true;
    },
    [completeLinkedAccount, enablePreinstallChecklist]
  );

  const showLinkUnavailable = useCallback(() => {
    window.localStorage.removeItem(SIGNUP_REFLINK_STORAGE_KEY);
    window.localStorage.removeItem(PENDING_CONTACT_ID_STORAGE_KEY);
    setMessage(
      "This signup link has already been used or has expired. Continue to sign in."
    );
    setStatus("linkUnavailable");
  }, []);

  const finishWithCurrentApiAccess = useCallback(async () => {
    if (isLinking.current) {
      return;
    }

    isLinking.current = true;
    try {
      const linkedContactId = window.localStorage.getItem(
        PENDING_CONTACT_ID_STORAGE_KEY
      );
      if (!linkedContactId) {
        showLinkUnavailable();
        return;
      }

      setStatus("checking");
      setMessage("");
      const authProbe = await api.fetchApi(
        "/?ph=a&target=b",
        "get",
        {},
        {
          Accept: "application/json",
        }
      );
      if (authProbe == null || authProbe.status !== 200) {
        setStatus("needsAuth");
        return;
      }

      const preinstallEnabled = await enablePreinstallChecklist(
        linkedContactId
      );
      if (!preinstallEnabled) {
        setMessage("Unable to enable the pre-install checklist.");
        setStatus("error");
        return;
      }
      completeLinkedAccount(linkedContactId);
    } catch (e) {
      setMessage(e.message || "Unable to finish account setup.");
      setStatus("error");
    } finally {
      isLinking.current = false;
    }
  }, [completeLinkedAccount, enablePreinstallChecklist, showLinkUnavailable]);

  const linkAccount = useCallback(async () => {
    if (isLinking.current) {
      return;
    }

    isLinking.current = true;
    try {
      if (!reflink) {
        setMessage("Missing signup reference link.");
        setStatus("error");
        return;
      }

      setStatus("checking");
      setMessage("");
      window.localStorage.setItem(SIGNUP_REFLINK_STORAGE_KEY, reflink);
      let usedFirebaseAccess = false;

      const authProbe = await api.fetchApi(
        "/?ph=a&target=b",
        "get",
        {},
        {
          Accept: "application/json",
        }
      );

      if (authProbe == null || authProbe.status !== 200) {
        const hasFirebaseAccess = await restoreApiAccessFromFirebase();
        if (!hasFirebaseAccess) {
          setStatus("needsAuth");
          return;
        }
        enableFirebaseAuthMode();
        usedFirebaseAccess = true;
      }

      const linkResponse = await api.post("me.1/linkaccount", { reflink });

      if (linkResponse?.success === true || linkResponse?.error === 0) {
        const linkedContactId = linkResponse?.contactid;
        if (!linkedContactId) {
          await finishLinkedAccount(linkedContactId);
          return;
        }
        const preinstallEnabled = await enablePreinstallChecklist(
          linkedContactId
        );
        if (!preinstallEnabled) {
          window.localStorage.setItem(
            PENDING_CONTACT_ID_STORAGE_KEY,
            linkedContactId
          );
          setMessage("Unable to enable the pre-install checklist.");
          setStatus("error");
          return;
        }
        window.localStorage.removeItem(SIGNUP_REFLINK_STORAGE_KEY);
        if (usedFirebaseAccess) {
          await finishLinkedAccount(linkedContactId, true);
          return;
        }
        completeLinkedAccount(linkedContactId);
      } else {
        const responseMessage =
          linkResponse?.message || "Unable to link this account.";
        if (responseMessage === INVALID_REFLINK_MESSAGE) {
          const linkedContactId = window.localStorage.getItem(
            PENDING_CONTACT_ID_STORAGE_KEY
          );
          if (linkedContactId) {
            const finished = await finishLinkedAccount(linkedContactId);
            if (finished) {
              return;
            }
          }
          showLinkUnavailable();
          return;
        }
        setMessage(responseMessage);
        setStatus("error");
      }
    } catch (e) {
      setMessage(e.message || "Unable to link this account.");
      setStatus("error");
    } finally {
      isLinking.current = false;
    }
  }, [
    completeLinkedAccount,
    enablePreinstallChecklist,
    finishLinkedAccount,
    reflink,
    showLinkUnavailable,
  ]);

  useEffect(() => {
    if (didAutoStart.current) {
      return;
    }
    didAutoStart.current = true;
    if (window.localStorage.getItem(PENDING_CONTACT_ID_STORAGE_KEY)) {
      finishWithCurrentApiAccess();
      return;
    }
    linkAccount();
  }, [finishWithCurrentApiAccess, linkAccount]);

  if (status === "checking") {
    return <LoadingBox />;
  }

  if (status === "error") {
    return (
      <ErrorBox>
        <p>{message}</p>
        {reflink ||
        window.localStorage.getItem(PENDING_CONTACT_ID_STORAGE_KEY) ? (
          <Button
            color="primary"
            onClick={
              window.localStorage.getItem(PENDING_CONTACT_ID_STORAGE_KEY)
                ? finishWithCurrentApiAccess
                : linkAccount
            }
          >
            Try again
          </Button>
        ) : null}
      </ErrorBox>
    );
  }

  if (status === "linkUnavailable") {
    return (
      <ErrorBox>
        <p>{message}</p>
        <Button
          color="primary"
          onClick={() =>
            window.location.replace(config.MYACCOUNT.APP_PATH + "/login")
          }
        >
          Continue to sign in
        </Button>
      </ErrorBox>
    );
  }

  if (status === "needsAuth") {
    return (
      <OAuthLogin
        title="Sign in to finish setup"
        message="This signup needs to be linked to a MyAccount login."
        onAuthenticated={
          window.localStorage.getItem(PENDING_CONTACT_ID_STORAGE_KEY)
            ? finishWithCurrentApiAccess
            : linkAccount
        }
        restoreOnMount={false}
        legacyReturnPath={legacyReturnPath}
      />
    );
  }
  return <LoadingBox />;
};

export default NewUserLinkAccount;
